// components/estimates/EstimateSummaryCard.js
// Card riepilogo totali preventivo

import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Calculator } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

// Format percentage with max 2 decimals
const formatPercentage = (value) => {
  if (!value || value === 0) return '0';
  const rounded = Math.round(value * 100) / 100;
  return rounded.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const EstimateSummaryCard = ({
  categoriesWithItems,
  categoryTotals,
  grandTotal,
  formData,
  promotions,
  worksTotals,
  onWorksDiscountChange,
  onWorksClosedPriceChange
}) => {
  const worksMode = formData?.works_discount_mode || 'percentage';
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="w-5 h-5" />
          Riepilogo Preventivo
        </CardTitle>
      </CardHeader>
      <CardContent>
        {categoriesWithItems.length > 0 ? (
          <div className="space-y-2" data-testid="estimate-summary">
            {/* Category summary */}
            {categoriesWithItems.map(cat => {
              const totals = categoryTotals[cat.code];
              const isWorks = ['C', 'D', 'E'].includes(cat.code);
              const hasDiscount = !isWorks && totals.discountAmount > 0;
              return (
                <div key={cat.code} className="flex justify-between items-center py-2 border-b border-[#F2F2F2]">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{cat.code} - {cat.label}</span>
                    <span className="text-sm text-[#737373]">({totals.itemCount} voci)</span>
                  </div>
                  <div className="flex items-center gap-4">
                    {hasDiscount && (
                      <span className="text-sm text-red-500">-{formatPercentage(totals.discountPercentageDisplay)}%</span>
                    )}
                    <span className="font-medium" data-testid={`summary-cat-total-${cat.code}`}>
                      {formatCurrency(isWorks ? totals.subtotal : totals.discountedTotal)}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* === BLOCCO UNICO SCONTO LAVORI C+D+E === */}
            {worksTotals?.present && (
              <div className="mt-4 p-4 rounded-lg bg-[#FAFAFA] border border-[#EDEDED]" data-testid="works-discount-block">
                <div className="flex justify-between items-center mb-3">
                  <span className="font-semibold text-[#3C3C3C]">Lavori edili e impianti</span>
                  <span className="font-semibold" data-testid="works-base-total">{formatCurrency(worksTotals.base)}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#737373] block mb-1">Sconto %</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0"
                      value={worksMode === 'percentage' && formData.works_discount_percentage ? formData.works_discount_percentage : ''}
                      onChange={(e) => onWorksDiscountChange(e.target.value)}
                      data-testid="works-discount-percentage-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#737373] block mb-1">oppure Prezzo chiuso lavori €</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder={formatCurrency(worksTotals.base)}
                      value={worksMode === 'closed_price' && (formData.works_closed_price ?? '') !== '' ? formData.works_closed_price : ''}
                      onChange={(e) => onWorksClosedPriceChange(e.target.value)}
                      data-testid="works-closed-price-input"
                    />
                  </div>
                </div>
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-[#EDEDED]">
                  <span className="text-sm font-medium text-[#3C3C3C]">Totale lavori scontato</span>
                  <div className="flex items-center gap-3">
                    {worksTotals.hasDiscount && (
                      <span className="text-sm text-red-500">-{formatPercentage(worksTotals.percentageDisplay)}%</span>
                    )}
                    <span className="font-bold text-[#FF7819]" data-testid="works-discounted-total">
                      {formatCurrency(worksTotals.netTotal)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Grand totals */}
            <div className="pt-4 space-y-2">
              <div className="flex justify-between items-center text-[#737373]">
                <span>Subtotale</span>
                <span>{formatCurrency(grandTotal.subtotal)}</span>
              </div>
              {grandTotal.worksDiscount > 0 && (
                <div className="flex justify-between items-center text-red-500">
                  <span>Sconto lavori</span>
                  <span data-testid="summary-works-discount">-{formatCurrency(grandTotal.worksDiscount)}</span>
                </div>
              )}
              {(grandTotal.categoryDiscount - (grandTotal.worksDiscount || 0)) > 0 && (
                <div className="flex justify-between items-center text-red-500">
                  <span>Sconti categoria</span>
                  <span>-{formatCurrency(grandTotal.categoryDiscount - (grandTotal.worksDiscount || 0))}</span>
                </div>
              )}
              {grandTotal.promoDiscount > 0 && (
                <div className="flex justify-between items-center text-[#FF7819]">
                  <span>
                    Sconto promozione ({promotions.find(p => p.id === formData.promotion_id)?.name || ''} -{formatPercentage(grandTotal.promoPercentage)}%)
                  </span>
                  <span>-{formatCurrency(grandTotal.promoDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t-2 border-[#FF7819]">
                <span className="text-lg font-bold text-[#3C3C3C]">TOTALE INVESTIMENTO</span>
                <span className="text-xl font-bold text-[#FF7819]" data-testid="grand-total">
                  {formatCurrency(grandTotal.total)}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-[#737373]">
            <p>Seleziona una categoria e aggiungi voci per vedere il riepilogo</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EstimateSummaryCard;
